import { LitElement, html, css } from 'lit';
import { classMap } from 'lit-html/directives/class-map.js';
import { LIT_CSS_VARIABLES, GRID_LAYOUT } from '../lib/lit-styles.js';
import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '../lib/lit-3form-block/styles.js';
import Vimeo from '@vimeo/player';
import { handleAdobeNavEvent } from './adobe-analytics';
import { VideoAnalytics } from './video-analytics';

// Handles share urls (vimeo.com/123) and embed urls
// (player.vimeo.com/video/123).
const VIMEO_URL_REGEX = /^https?:\/\/(?:.*\.)?vimeo\.com\/(?:video\/)?(\d+)(?:[/?#]|$)/;

// Null-safe vimeo id extraction; returns '' for non-vimeo or malformed urls.
export const vimeoIdFrom = url => {
  if (!url || !url.includes('vimeo')) return '';
  const match = url.match(VIMEO_URL_REGEX);
  return match ? match[1] : '';
};

export class VimeoVideo extends LitElement {
  static get properties() {
    return {
      videoId: { type: Number },
      videoWidth: { type: Number },
      minHeight: { type: Number },
      fullHeight: { type: Boolean },
      ctaAnalyticsData: { type: Object },
      videoThumb: { type: String },
      videoThumbClicked: { type: Boolean },
      player: { type: Vimeo },
    };
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      GRID_LAYOUT,
      css`
        div#video-player-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          background-color: #000;
        }

        iframe {
          max-height: 100%;
          max-width: 100%;
        }

        .hide {
          display: none !important;
        }

        #frame {
          position: relative;
          width: 100%;
        }

        #video-player {
          width: 100%;
        }

        #video-player.full-height {
          height: 100%;
        }

        #frame img {
          display: block;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          max-width: 100%;
          max-height: 100%;
        }

        /* Opt in with the fitaspect attribute: reserve the exact box the
           responsive iframe will take so swapping the thumbnail for the
           player does not move anything below it. */
        #video-player-wrapper.fit-aspect {
          height: auto;
          aspect-ratio: var(--vimeo-aspect-ratio, 16 / 9);
        }

        .fit-aspect #frame {
          height: 100%;
        }

        /* Fill the reserved box, but stay out of the flow so the play
           button still lands over the thumbnail. */
        .fit-aspect #frame img {
          top: 0;
          left: 0;
          transform: none;
          width: 100%;
          height: 100%;
          max-height: none;
          object-fit: contain;
        }

        /* Opt in with letterbox-portrait: a portrait video gets the same
           16 / 9 box as a landscape one and the player letterboxes it. The
           embed's responsive wrapper carries an inline portrait
           padding-bottom, hence the !important. */
        :host([portrait][letterbox-portrait]) #video-player-wrapper.fit-aspect {
          aspect-ratio: 16 / 9;
        }

        :host([portrait][letterbox-portrait]) #video-player > div {
          padding: 56.25% 0 0 0 !important;
        }

        .play-button {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100px;
          height: 70px;
          background-color: rgba(0, 0, 0, 0.8);
          border-radius: 5px;
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
          border: none;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }

        .play-icon {
          width: 0;
          height: 0;
          border-left: 25px solid rgba(255, 255, 255, 0.8);
          border-top: 15px solid transparent;
          border-bottom: 15px solid transparent;
          transition: border-left-color 0.3s ease;
        }

        #frame:hover {
          cursor: pointer;
        }

        #frame:hover .play-button {
          background-color: rgba(255, 153, 61, 1);
        }

        #frame:hover .play-icon {
          border-left: 25px solid rgba(255, 255, 255, 1);
        }
      `,
    ];
  }

  constructor() {
    super();
    this.ctaAnalyticsData = {};
    this.videoThumb = '';
    this.videoThumbClicked = false;
    this.player = null;
    this.videoTitle = '';
    this.videoWidth = null;
    this.minHeight = null;
    this.fullHeight = true;
  }

  connectedCallback() {
    super.connectedCallback();

    const hostElement = this.shadowRoot.host;
    if (hostElement?.id === 'hero-media') {
      // The hero-media id comes from the Walter carousel and requires the
      // video player to be positioned differently.
      this.fullHeight = false;
    }

    const checkInterval = setInterval(() => {
      if (this.shadowRoot.querySelector('div#video-player-wrapper')) {
        this.setupThumbnailCover();
        this.setupVideoPlayer();
        this.setupMinHeightConstraint();
        clearInterval(checkInterval);
      }
    }, 50);
  }

  render() {
    return html`
      <div
        id="video-player-wrapper"
        class="${classMap({ 'fit-aspect': this.fitsVideoAspect })}"
      >
        <div
          id="video-player"
          class="${classMap({
            show: this.videoThumbClicked,
            hide: !this.videoThumbClicked,
            'full-Height': this.fullHeight,
          })}"
        ></div>
        <div
          id="frame"
          @click="${this.handleVideoThumbClick}"
          class="${classMap({
            show: !this.videoThumbClicked,
            hide: this.videoThumbClicked,
          })}"
        >
          <img
            id="videoThumbnail"
            referrerpolicy="origin"
            decoding="async"
            loading="lazy"
            src="${this.videoThumb}"
            aria-label="Play: Video"
            alt="Play: Video"
          />
          <button class="play-button" aria-label="Play">
            <span class="play-icon" aria-hidden="true"></span>
          </button>
        </div>
      </div>
    `;
  }

  async setupVideoPlayer() {
    const videoPlayer = this.shadowRoot.querySelector('div#video-player');

    const options = {
      id: this.videoId,
      responsive: true,
    };

    this.player = new Vimeo(videoPlayer, options);
    this.setupVideoAnalytics();
  }

  setupVideoAnalytics() {
    const analytics = new VideoAnalytics(() => this.videoAnalyticsParts);

    this.player.on('play', () => analytics.play());
    this.player.on('timeupdate', ({ seconds, percent }) =>
      analytics.timeUpdate(seconds, percent)
    );
    this.player.on('pause', ({ percent }) => analytics.pause(percent));
    this.player.on('ended', () => analytics.complete());
  }

  // Category|ID|Name, the order of the eVar70 classifications.
  get videoAnalyticsParts() {
    const category =
      this.ctaAnalyticsData?.placement ||
      this.getRootNode()?.host?.analyticsBlockLabel ||
      'Video';
    const id = this.parsedVideoId || '';
    return { category, id, title: this.videoTitle || `Vimeo ${id}` };
  }

  get showVideoThumb() {
    return !!this.videoThumb;
  }

  handleVideoThumbClick() {
    this.videoThumbClicked = !this.videoThumbClicked;
    this.player.play();
    handleAdobeNavEvent({
      navString: `INP|Video Play|${this.videoAnalyticsLabel}`,
    });
  }

  get videoAnalyticsLabel() {
    return (
      this.ctaAnalyticsData?.placement || `Vimeo ${this.parsedVideoId || ''}`
    );
  }

  async setupThumbnailCover() {
    if (this.parsedVideoId) {
      await fetch(`https://vimeo.com/api/v2/video/${this.parsedVideoId}.json`)
        .then(response => {
          if (response.ok) {
            return response.json();
          }
        })
        .then(body => {
          if (body[0]) {
            this.videoTitle = body[0].title || '';
            const width = `_${this.parsedVideoWidth}`;
            this.videoThumb = body[0].thumbnail_large.replace('_640', width);
            this.setAspectRatio(body[0]);
          }
        });
    }
  }

  get fitsVideoAspect() {
    return this.hasAttribute('fitaspect');
  }

  // 16 / 9 covers the box until the json lands; correct it once we know the
  // video's own dimensions, otherwise the player still resizes on load.
  setAspectRatio({ width, height }) {
    if (!this.fitsVideoAspect || !width || !height) return;

    this.style.setProperty('--vimeo-aspect-ratio', `${width} / ${height}`);
    // Blocks can key off this attribute to cap a portrait video's height.
    this.toggleAttribute('portrait', height > width);
  }

  get parsedVideoId() {
    if (this.videoId) return this.videoId;

    this.videoId = this.getAttribute('videoid')
      ? JSON.parse(this.getAttribute('videoid'))
      : null;

    return this.videoId;
  }

  get parsedVideoWidth() {
    if (this.videoWidth) return this.videoWidth;

    this.videoWidth = this.getAttribute('videoWidth')
      ? JSON.parse(this.getAttribute('videoWidth'))
      : 1000;

    return this.videoWidth;
  }

  get parsedMinHeight() {
    if (this.minHeight) return this.minHeight;

    this.minHeight = this.getAttribute('minHeight')
      ? JSON.parse(this.getAttribute('minHeight'))
      : 350;

    return this.minHeight;
  }

  setupMinHeightConstraint() {
    if (this.fitsVideoAspect) return;

    if (this.parsedMinHeight) {
      this.shadowRoot.querySelector(
        'div#video-player-wrapper'
      ).style.minHeight = `${this.minHeight}px`;
      this.shadowRoot.querySelector(
        'div#frame'
      ).style.height = `${this.minHeight}px`;
      this.requestUpdate();
    }
  }
}
customElements.define('vimeo-video', VimeoVideo);
